/**
 * /ja/comparisons/obsidian — Draftlize vs Obsidian (JA). オブシディアン 2.3k.
 * Links to /ja/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function ObsidianJa() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/comparisons/obsidian"
      hreflangLocales={['ja']}
      documentTitle="Obsidianでプロダクト管理は限界？Draftlizeとの違い · Draftlize"
      documentDescription="Obsidianは個人のノートに強い。チームの「決定」を生きた状態で共有するなら？ DraftlizeとObsidianの違いを解説します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">比較 · vs Obsidian</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Obsidian vs Draftlize——<br />
            <em>個人ノートと、チームの決定。</em>
          </h1>
          <p className="lede">
            Obsidian はローカルの Markdown で個人の知識を積み上げる、最高のノート金庫です。ファイルを所有し、オフラインで書き、グラフでつなぐ。Draftlize は別の問題を解きます——チームの<em>決定</em>を、AI エージェントが読み書きできる構造化カードとして生かし続けます。Obsidian が「個人の第二の脳」なら、Draftlize は「チームの決定基盤」です。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">要点</span>
            <h2 className="serif">並べて比較、<em>7つの観点。</em></h2>
          </div>
          <p>個人の知識整理か、チームの決定管理か——そもそも問題が違います。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Obsidian</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>基盤の形</td><td className="ours yes">型付き決定カードグラフ</td><td className="partial">ローカル Markdown 金庫</td></tr>
            <tr><td>ローカル優先・ファイル所有・オフライン</td><td className="no">クラウド</td><td className="yes">優秀</td></tr>
            <tr><td>個人知識・バックリンク・グラフビュー</td><td className="partial">決定中心</td><td className="yes">優秀</td></tr>
            <tr><td>プラグインのエコシステム</td><td className="no">—</td><td className="yes">膨大</td></tr>
            <tr><td>AI が決定を文脈として読む</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
            <tr><td>依存が変わると stale 伝播</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
            <tr><td>Claude Code / MCP 連携</td><td className="ours yes">ネイティブ</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>Obsidian が勝つ</em>ところ。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">ファイルを所有したいなら</h3>
            <p>Obsidian はローカル Markdown。データは自分のディスクにあり、オフラインで書け、どのサービスにも縛られません。これが大事なら Obsidian。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">個人知識管理（PKM）なら</h3>
            <p>バックリンク、グラフビュー、デイリーノート——Obsidian の個人知識管理は最高峰です。Draftlize は個人ノートアプリを目指していません。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">プラグインでカスタマイズ</h3>
            <p>数百のコミュニティプラグインで自在に拡張できます。Draftlize はその拡張性を狙っていません。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize が勝つ</em>ところ。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">チームの決定を共有する</h3>
            <p>Obsidian は基本的に一人用のノートです。Draftlize はチームの決定を参照できるカードとして共有します——誰がいつ何を、なぜ決めたか、何がそれに依存するか。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">決定が古くなると知らせる</h3>
            <p>Obsidian のノートを変えても誰も気づかない。Draftlize の決定カードを変えると、依存するカードが自動で <code>stale</code> に。ビルドシステムが無効化を追うように。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI エージェントが読み書きする</h3>
            <p>Obsidian は人が読む金庫。Draftlize のカードは MCP 経由で Claude Code・Cursor が読み書きします。詳しくは <a href="/ja/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>意思決定ログ</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Obsidian は「自分の知識をどこに積むか」の答え。Draftlize は「チームの決定をどう生かし続けるか」の答え。
        </blockquote>
        <cite>個人知識が目的なら、Obsidian で足ります。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">$5 無料で<em>始める。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Obsidian は個人ノートとして使い続けて。チームの決定は Draftlize で生きたカードに。新規登録で $5、使った分だけ、残高は無期限。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
